import React, { useRef } from "react";
import {  RCCButton, RCCDropDownItem, RCCGrid, RCCIcon, RCCTooltip } from "react-common-components";
import CheckPermission from "src/components/CheckPermission";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import ListCards from "src/components/ListCards";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import { IKanbanType } from "src/pages/Actions/Kanban/kanbanModes/MultipleKanbans/interface";
import { IProject } from "../Project/interface";
import CardProjects from "./Components/CardProjects";
import ListProjects from "./Components/ListProjects";
import { ProjectSituationFilter } from "./Components/ListProjectsLevel/interface";
import { IListOfProject, ProjectModel } from "./interface";
import ModalAttachmentsProjects, { IRefModalAttachmentsProjects } from "./modalAttachments";


export const getColorProjectSituation = (situation?: any) => {
	switch (situation) {
	case ProjectSituationFilter.NOT_STARTED:
		return "purple";
	case ProjectSituationFilter.IN_PROGRESS:
		return "blue";
	case ProjectSituationFilter.NEXT_TO_EXPIRE:
		return "yellow";
	case ProjectSituationFilter.DELAYED:
		return "red";
	case ProjectSituationFilter.FINISHED:
		return "green";
	default:
		return "grey";
	}
};

const ListOfProjects: React.FC<IListOfProject> = ({
	projects,
	filterList,
	layoutList,
	onCallbackProgram,
	onCallbackChangeProject,
	openModalChangeProject,
	openModalDeleteProject,
	openModalAction,
	openModalAddSprint,
	openModalChangeSprint,
	openModalDetails,
	openModalReleaseActions,
	showBudget,
	openModalGantt
}) => {

	const { translate } = useTranslation();
	const { superUser, administrator, checkIfHasSpecificPermission } = useAuth();

	const isAdminOrSuper = administrator || superUser;
	const canCreateUpdate = checkIfHasSpecificPermission("/projects/panel", PermissionEnum.CREATE_UPDATE);
	const canEditAttachments = isAdminOrSuper || canCreateUpdate;

	const refAttachments = useRef<IRefModalAttachmentsProjects>(null);

	const options = (row: IProject) => (
		<RCCGrid container item justifyContent="flex-end">
			<RCCTooltip
				color="grey" 
				text={`${translate("Visualizar")} Gantt`}
			>
				<RCCButton 
					color="transparent"
					icon={{
						name: "table",
						color: "var(--input-font-color)",
						size: 14
					}}
					styleInline="padding: 5px 5px;"
					onClick={(e) => {e.stopPropagation(); if(!openModalGantt) return; openModalGantt(row.pra002Id, `${translate("Projeto")}: ${row.fullTitle}`);}}
				/>
			</RCCTooltip>
			<RCCTooltip
				color="grey" 
				text={translate("Abrir Kanban")}
			>
				<RCCButton 
					color="transparent"
					icon={{
						name: "kanban",
						color: "var(--input-font-color)",
						size: 14
					}}
					styleInline="padding: 5px 5px;"
					onClick={(e) => { 
						if(row.pra002Kba001Id)
							window.open(`/actions/kanban/${row.pra002Kba001Id}?association=${IKanbanType.PROJECT}&associationId=${row.pra002Id}`);
						else
							window.open(`/actions/kanban?defaultKanban=true&project=${row.pra002Id}`);
						e.stopPropagation(); 
					}}
				/>
			</RCCTooltip>
		
			{(superUser || administrator || row.permission >= 1)  && (
				<OptionsColumn useStopPropagation>	
					{(superUser || administrator || row.permission >= 2)  && (
						<>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								{row.pra002Modelo === ProjectModel.AGILE && (
									<RCCDropDownItem
										selfEnclose
										onClick={(e) => {e.stopPropagation(); if(!openModalAddSprint) return; openModalAddSprint(row);  }}
									>
										<RCCIcon name="plus" />
										{translate("Adicionar Sprint")}
									</RCCDropDownItem>
								)}
								<RCCDropDownItem
									selfEnclose
									onClick={(e) => {e.stopPropagation(); if(!openModalChangeProject) return; openModalChangeProject(row.pra002Id, false);}}
								>
									<RCCIcon name="pencil" />
									{translate("Alterar")}
								</RCCDropDownItem>
								<RCCDropDownItem
									selfEnclose
									onClick={(e) => {e.stopPropagation(); if(!openModalChangeProject) return; openModalChangeProject(row.pra002Id, true);}}
								>
									<RCCIcon name="copy" />
									{translate("Copiar")}
								</RCCDropDownItem>
							</CheckPermission>
						</>
					)}
					
					<RCCDropDownItem
						selfEnclose
						onClick={(e) => { e.stopPropagation(); refAttachments.current?.actionButtonAttachments(row.pra002Id, row.fullTitle, undefined, !canEditAttachments);}}
					>
						<RCCIcon name="attachment" />
						{translate("Anexos")}
					</RCCDropDownItem>

					<RCCDropDownItem
						selfEnclose
						onClick={(e) => { e.stopPropagation(); showBudget(16, row.fullTitle, row.pra002Pra001Id, row.pra002Id);}}
					>
						<RCCIcon name="dollar" />
						{translate("Orçamentos")}
					</RCCDropDownItem>

					<RCCDropDownItem
						selfEnclose
						onClick={(e) => { e.stopPropagation(); if(!openModalDetails) return; openModalDetails(row.pra002Id, "project", row.pra002Titulo, row.pra002ControlaHoras === "S");}}
					>
						<RCCIcon name="info" />
						{translate("Detalhes")}
					</RCCDropDownItem>

					{(superUser || administrator || row.permission >= 2)  && (
						<>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
								<RCCDropDownItem 
									selfEnclose
									onClick={(e) => {
										e.stopPropagation(); 
										if(!openModalDeleteProject){
											return;
										} 
										openModalDeleteProject(row.pra002Id, row.fullTitle, row.pra002Pra001Id);
									}}
								>
									<RCCIcon name="trash" />
									{translate("Excluir")}	
								</RCCDropDownItem>
							</CheckPermission>	
						</>
					)}
				</OptionsColumn>
			)}
		</RCCGrid>
	);

	return (
		<>
				
			{layoutList === "card" ? (
				<ListCards
					data={projects}
					mapper={(project: IProject) => (
						<CardProjects
							colorSituation={getColorProjectSituation(String(project.situation))}
							project={project}
							options={options}
							key={`list-project-cards-panel-${project.pra002Id}`}
						/>
					)}
					filter={filterList ? (project) => String(project.situation) === filterList : undefined}		
				/>
			): (
				<>
					<ul className="ul-project-head">
						<li className="project-head">
							<RCCGrid item xs={4} alignItems="center"> {translate("Projeto")} - {translate("Fase")} - {translate("Ação")}</RCCGrid>
							<RCCGrid item xs={2}>{translate("Responsável")}</RCCGrid>
							<RCCGrid item xs={2}>{translate("Período")}</RCCGrid>
							<RCCGrid item xs={1}>{translate("Concluído")} <small>%</small></RCCGrid>
							<RCCGrid item xs={1} className="head-title">{translate("Previsto")} <small>%</small></RCCGrid>
							<RCCGrid item xs={1} styleInline="text-align: center" className="head-title">{translate("Ações")}</RCCGrid>
							<RCCGrid item xs={1}> </RCCGrid>
						</li>
					</ul>
					
					{projects.map((project: IProject) => <ListProjects 
						key={`list-project-list-panel-${project.pra002Id}`}
						options={options}
						colorSituation={getColorProjectSituation(String(project.situation))}
						project={project}
						filterList={filterList}
						onCallbackProgram={onCallbackProgram}
						onCallbackChangeProject={onCallbackChangeProject}
						openModalChangeProject={openModalChangeProject}
						openModalDeleteProject={openModalDeleteProject}
						openModalAction={openModalAction}
						openModalAddSprint={openModalAddSprint}
						openModalChangeSprint={openModalChangeSprint}
						openModalDetails={openModalDetails}
						openModalReleaseActions={openModalReleaseActions}
						openModalGantt={openModalGantt}
					/>)
					}
				</>
			)}	

			<ModalAttachmentsProjects ref={refAttachments} />
		</>
	);
};

export default ListOfProjects;
